import React, { useCallback, useEffect, useState } from 'react'
import { When } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { CircleCloseIcon } from '@/atoms/Icons/CircleCloseIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { Image } from '@/atoms/Image'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { CaptionSM, LabelSM, TitleXXS } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { getWebClient } from '@/services/ApolloClient'
import { useSaleBanner } from '@/services/ContentfulSaleService/hooks'
import { useGuildMember, useUserGuildTickets } from '@/services/GuildUserService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'
import { getBooleanFromLocalStorage, writeToLocalStorage } from '@/utils/local-storage'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'

export interface TicketsNavBannerProps {
  hide?: boolean
}

export const TicketsNavBanner: React.FC<TicketsNavBannerProps> = ({ hide = false }) => {
  const track = useSafeTrack()
  const { t } = useTranslate('common')
  const router = useSafeRouter()
  const { loading: bannerLoading, saleWithBanner } = useSaleBanner()
  const { isGuildMember, loading } = useGuildMember()
  const client = getWebClient()
  const [shouldRender, setShouldRender] = useState(false)
  const { numberAvailable: soloMioGuildTicketsCount = 0, loading: loadingGuildTickets } = useUserGuildTickets({
    theatricalSlug: slugs.soloMio,
    client,
    skip: !isGuildMember,
  })

  useEffect(() => {
    const hasDismissedSoloMioBanner = getBooleanFromLocalStorage('hasDismissedSoloMioNavBanner') ?? false
    const isGuildJoinPage = router.pathname.includes('/guild/join')
    const isGuildPricingPage = router.pathname.includes('/guild/pricing')
    const isGuildCheckoutPage = router.pathname.includes('/guild/checkout')
    const isMarketingOfferPage = router.pathname.includes('/m/offer')

    if (hide || isGuildJoinPage || isGuildPricingPage || isGuildCheckoutPage || isMarketingOfferPage) {
      setShouldRender(false)
    } else if (!loading && !bannerLoading && (isGuildMember || !saleWithBanner)) {
      // Show Solo Mio banner if not dismissed
      if (!hasDismissedSoloMioBanner) {
        setShouldRender(true)
      } else {
        setShouldRender(false)
      }
    }
  }, [hide, router.pathname, isGuildMember, loading, bannerLoading, saleWithBanner])

  const handleTicketsNavBannerDismiss = useCallback(
    (buttonOrigin: boolean, e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault()
        e.stopPropagation()
      }
      writeToLocalStorage('hasDismissedSoloMioNavBanner', true)
      if (!buttonOrigin) {
        track('Tickets Nav Banner Dismissed', { banner: 'Solo Mio' })
      }
      setShouldRender(false)
    },
    [track],
  )

  const handleTicketsNavButtonClick = useCallback(() => {
    track('Tickets Nav Banner Button Clicked', { banner: 'Solo Mio' })
    handleTicketsNavBannerDismiss(true)
  }, [handleTicketsNavBannerDismiss, track])

  return (
    <When condition={shouldRender}>
      <a
        className="group-floating-cta fixed inset-x-0 bottom-0 z-40 flex max-w-full cursor-pointer flex-col items-center bg-black md:absolute md:bottom-auto md:top-0 md:z-50"
        onClick={handleTicketsNavButtonClick}
        href={`${paths.tickets.index}/${slugs.soloMio}`}
      >
        <div className="responsive relative h-14 w-full max-w-[1728px] p-3 md:h-16 md:p-4">
          <ResponsiveBackgroundImage
            src={getCloudinaryImageUrl({
              path: 'v1768490332/angel-studios/nav/Promotional-Banner-Solo-BG-Mobile-Image-01-XL.webp',
              width: 1024,
            })}
            className="absolute inset-0 h-full w-full bg-cover bg-top lg:hidden"
          />
          <ResponsiveBackgroundImage
            src={getCloudinaryImageUrl({
              path: 'v1768490274/angel-studios/nav/solo_mio_desktop_background.webp',
              width: 1728,
            })}
            className="absolute inset-0 hidden h-full w-full bg-cover bg-no-repeat lg:block"
          />
          <div className="relative mx-auto flex h-full w-full max-w-[1728px] items-center justify-between">
            <div className="flex flex-row md:gap-[27px]">
              <div className="flex flex-col items-start gap-2 lg:flex-row lg:items-center lg:gap-[27px]">
                <Image
                  src={getCloudinaryImageUrl({
                    path: 'v1768490224/angel-studios/nav/Solo_Mio_-_LOCKED_-_Gold_and_White_2.webp',
                    width: 200,
                  })}
                  alt={'Solo Mio Logo'}
                  className="max-h-[14px] max-w-[105px] md:max-h-[24px] md:max-w-[185px]"
                  width={185}
                  height={24}
                />
                <CaptionSM
                  weight="bold"
                  className="lg:photon-title-xs !text-white [text-shadow:_0_1px_2px_rgb(0_0_0_/_80%)] lg:[text-shadow:none]"
                >
                  {t('ticketsAvailableNow', 'Tickets Available Now')}
                </CaptionSM>
              </div>
              <Image
                src={getCloudinaryImageUrl({
                  path: 'v1768490107/angel-studios/nav/Promo_Tickets_Solo_Mio.webp',
                  width: 104,
                })}
                alt={'Solo Mio Tickets'}
                className="hidden md:block"
                width={104}
                height={48}
              />
            </div>
            <div className="flex flex-row items-center gap-[35px] md:gap-[42px] lg:gap-[145px]">
              <div>
                <When condition={!loadingGuildTickets}>
                  <LinkButton
                    href={`${paths.tickets.index}/${slugs.soloMio}`}
                    variant={'black'}
                    className="rounded-lg border-transparent bg-white/20 px-4 py-2 backdrop-blur-lg hover:bg-white/40"
                    onClick={handleTicketsNavButtonClick}
                  >
                    <LabelSM>
                      {soloMioGuildTicketsCount && soloMioGuildTicketsCount > 0
                        ? t('useGuildTickets', 'Use Guild Tickets')
                        : t('getShowtimes', 'Get Showtimes')}
                    </LabelSM>
                  </LinkButton>
                </When>
              </div>
              <div
                onClick={(e) => handleTicketsNavBannerDismiss(false, e)}
                className="cursor-pointer rounded-full lg:rounded-none"
              >
                <div className="hidden cursor-pointer lg:flex lg:flex-row lg:items-center lg:gap-2">
                  <TitleXXS weight="semibold" color="core-gray-200">
                    {t('close', 'Close')}
                  </TitleXXS>
                  <CloseIcon color="core-gray-200" size={24} className="cursor-pointer" />
                </div>
                <div className="lg:hidden">
                  <CircleCloseIcon color="white" size={21} className="cursor-pointer" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </a>

      {/* Spacer to push the site nav below the fixed banner on md+ */}
      <div className="hidden h-16 w-full md:block" aria-hidden="true" />
    </When>
  )
}
